import { NoteModel, SetNotePayloadModel } from '@/models/student-notes/note.model';
import { ErrorHandler } from '@/helpers/errorHandler';
import SentryClient from '@/helpers/sentry';
import {
  WESTEROS_AUTHORIZATION_ENUM,
  WESTEROS_MICROSERVICES_ENUM,
  westerosMutation,
  westerosQuery,
} from '@/helpers/westeros';
import RootState from '@/store/root_state';
import { ActionTree } from 'vuex';
import { NoteState } from './state';
import {
  getNotesByVideoId,
  getAllUserNotes,
  getLastVideoNotes,
  getSharedNotebookByHash,
  getClassNotesByVideoId,
} from '@/store/queries/westerosQueries';
import {
  removeNote,
  setNote,
  setSharedNotebook,
  updateNoteDexFields,
  copyNote,
} from '@/store/mutations/westerosMutations';
import i18n from '../../../i18n';
import { SharedNotebook } from '@/models/student-notes/shared-notebook.model';

interface DeleteResult {
  error?: Boolean;
  success?: Boolean;
  description: String;
}

const actions: ActionTree<NoteState, RootState> = {
  async GET_USER_VIDEO_NOTES({ commit, dispatch }, payload: { dexVideoId: number }): Promise<void> {
    const { dexVideoId } = payload;
    try {
      const { data } = await westerosQuery(
        WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
        getNotesByVideoId,
        {
          dexVideoId,
          deletedAt: null,
        },
        WESTEROS_AUTHORIZATION_ENUM.PROFILE,
      );

      if (!data?.getNotes) {
        ErrorHandler.showErrorMessage({
          error: new Error('No data returned from getUserNotes query'),
          defaultMessage: true,
        });
        return;
      }

      const notes = data.getNotes.data;
      commit('SET_VIDEO_NOTES', notes);

      const profilesIds = Array.from(new Set(notes.map((note: NoteModel) => note.profileId)));
      dispatch('GET_PROFILES_BY_IDS', profilesIds);
    } catch (e: any) {
      SentryClient.captureError(e);
    }
  },
  async GET_ALL_USER_NOTES(
    { commit },
    payload: {
      profileId: number;
      dexVideoId: number;
      dexLessonId: number;
      dexDisciplineId: number;
    },
  ): Promise<void> {
    commit('SET_LOADING_NOTEBOOK_NOTES', true);
    try {
      const { data } = await westerosQuery(
        WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
        getAllUserNotes,
        {
          ...payload,
          deletedAt: null,
        },
        WESTEROS_AUTHORIZATION_ENUM.DEX,
      );
      commit('SET_LOADING_NOTEBOOK_NOTES', false);

      if (!data?.getAllUserNotes) {
        ErrorHandler.showErrorMessage({
          error: new Error('No data returned from getAllUserNotes query'),
          defaultMessage: true,
        });
        return;
      }

      const groupedNotes = data.getAllUserNotes.data;
      commit('SET_NOTEBOOK_NOTES', groupedNotes);
    } catch (e: any) {
      SentryClient.captureError(e);
    }
  },
  async GET_LAST_VIDEO_NOTES(
    { commit },
    payload: {
      take: number;
    },
  ): Promise<void> {
    commit('SET_LOADING_NOTES', true);
    try {
      const { data } = await westerosQuery(
        WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
        getLastVideoNotes,
        {
          ...payload,
        },
        WESTEROS_AUTHORIZATION_ENUM.PROFILE,
      );

      commit('SET_LOADING_NOTES', false);

      if (!data?.getLastVideoNotes) {
        ErrorHandler.showErrorMessage({
          error: new Error('No data returned from getLastVideoNotes query'),
          defaultMessage: true,
        });
        return;
      }
      const notes = data.getLastVideoNotes.data;
      commit('SET_LAST_VIDEO_NOTES', notes);
    } catch (e: any) {
      SentryClient.captureError(e);
    }
  },
  async SET_USER_VIDEO_NOTE({ commit }, payload: SetNotePayloadModel): Promise<void> {
    const { saveNoteToClass, ...setNoteInput } = payload;
    const { data } = await westerosMutation(
      WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
      setNote,
      { ...setNoteInput },
      WESTEROS_AUTHORIZATION_ENUM.PROFILE,
    );

    if (!data?.setNote) {
      ErrorHandler.showErrorMessage({
        error: new Error('No data returned from setNote mutation'),
        defaultMessage: true,
      });
      return;
    }

    if (saveNoteToClass) {
      commit('SET_CLASS_VIDEO_NOTES', [data.setNote]);
    }
    commit('SET_VIDEO_NOTES', [data.setNote]);
  },
  async DELETE_USER_VIDEO_NOTE({ commit }, payload: { id: Number }): Promise<DeleteResult> {
    const { data } = await westerosMutation(
      WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
      removeNote,
      { ...payload },
      WESTEROS_AUTHORIZATION_ENUM.PROFILE,
    );

    if (!data?.removeNote) {
      ErrorHandler.showErrorMessage({
        error: new Error('No data returned from removeNote mutation'),
        defaultMessage: true,
      });
      return { error: true, description: i18n.tc('deletedAnnotationErrorMessage') };
    }

    commit('REMOVE_VIDEO_NOTE', payload.id);
    commit('REMOVE_CLASS_VIDEO_NOTE', payload.id);

    return { success: true, description: i18n.tc('deletedAnnotationSuccessMessage') };
  },
  async UPDATE_USER_NOTES_DEX_FIELDS(
    { commit },
    payload: {
      profileId: number;
      courseId: number;
    },
  ): Promise<void> {
    commit('SET_LOADING_NOTEBOOK_NOTES', true);
    const { data } = await westerosMutation(
      WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
      updateNoteDexFields,
      { ...payload },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
    );

    if (!data?.updateDexFields) {
      ErrorHandler.showErrorMessage({
        error: new Error('No data returned from updateDexFields mutation'),
        defaultMessage: true,
      });
    }
  },
  async GET_OTHER_STUDENT_SHARED_NOTEBOOK(
    { commit },
    payload: { notebookHash: string },
  ): Promise<void> {
    commit('SET_LOADING_SHARED_NOTEBOOK', true);
    const { notebookHash } = payload;
    try {
      const { data } = await westerosQuery(
        WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
        getSharedNotebookByHash,
        {
          generatedLink: notebookHash,
          deletedAt: null,
        },
        WESTEROS_AUTHORIZATION_ENUM.PROFILE,
      );

      commit('SET_LOADING_SHARED_NOTEBOOK', false);

      const notebook: SharedNotebook = data.getSharedNotebook;
      commit('SET_OTHER_STUDENT_SHARED_NOTEBOOK', notebook);
    } catch (e: any) {
      SentryClient.captureError(e);
    }
  },
  async SET_SHARED_NOTEBOOK({ commit }, dexDisciplineId): Promise<void> {
    commit('SET_GENERATING_SHARED_NOTEBOOK_LINK', true);
    const { data } = await westerosMutation(
      WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
      setSharedNotebook,
      { dexDisciplineId },
      WESTEROS_AUTHORIZATION_ENUM.PROFILE,
    );

    if (!data?.setSharedNotebook) {
      ErrorHandler.showErrorMessage({
        error: new Error('No data returned from setSharedNotebook mutation'),
        defaultMessage: true,
      });
      return;
    }

    const sharedNotebook: SharedNotebook = data.setSharedNotebook;
    commit('SET_SHARED_NOTEBOOK', sharedNotebook);
    commit('SET_GENERATING_SHARED_NOTEBOOK_LINK', false);
  },
  async GET_CLASS_VIDEO_NOTES(
    { commit, dispatch },
    payload: {
      dexVideoId: number;
    },
  ): Promise<void> {
    try {
      const { data } = await westerosQuery(
        WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
        getClassNotesByVideoId,
        payload,
        WESTEROS_AUTHORIZATION_ENUM.PROFILE,
      );

      if (!data?.getClassNotes) {
        ErrorHandler.showErrorMessage({
          error: new Error('No data returned from getClassNotes query'),
          defaultMessage: true,
        });
        return;
      }

      const notes = data.getClassNotes.data;
      commit('SET_CLASS_VIDEO_NOTES', notes);

      const profilesIds = Array.from(new Set(notes.map((note: NoteModel) => note.profileId)));
      dispatch('GET_PROFILES_BY_IDS', profilesIds);
    } catch (e: any) {
      SentryClient.captureError(e);
    }
  },
  async COPY_VIDEO_NOTE(
    { commit },
    payload: {
      id: number;
      originUrl: string;
    },
  ): Promise<void> {
    try {
      const { data } = await westerosMutation(
        WESTEROS_MICROSERVICES_ENUM.STUDENTNOTES,
        copyNote,
        { platform: 'dex', ...payload },
        WESTEROS_AUTHORIZATION_ENUM.PROFILE,
      );

      if (!data?.copyNote) {
        ErrorHandler.showErrorMessage({
          error: new Error('No data returned from copyNote mutation'),
          defaultMessage: true,
        });
        return;
      }

      commit('SET_VIDEO_NOTES', [data.copyNote]);
      commit('SET_CLASS_VIDEO_NOTES', [data.copyNote]);
    } catch (e: any) {
      SentryClient.captureError(e);
    }
  },
};

export default actions;
